import { useState, useEffect, useRef } from "react";
import { Camera, Shield, Zap, Check, ChevronDown } from "lucide-react";

export default function MockupBolt() {
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => setScrollY(container.scrollTop);
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div ref={containerRef} className="h-screen overflow-y-auto bg-[#0A0F1A] text-white" style={{ scrollBehavior: "smooth" }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-4" style={{ backgroundColor: scrollY > 50 ? "rgba(10,15,26,0.95)" : "transparent", backdropFilter: scrollY > 50 ? "blur(12px)" : "none", transition: "all 0.3s" }}>
        <img src="/logo-bolt.png" alt="TOOLIO" className="h-10" />
        <div className="flex gap-3">
          <button className="text-gray-400 text-sm">Sign in</button>
          <button className="bg-yellow-500 text-black text-sm font-bold px-4 py-1.5 rounded-full">Start</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-20 pb-16 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" style={{ transform: `translate(-50%, ${scrollY * -0.15}px)` }} />
        <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-yellow-500/5 rounded-full blur-3xl" />

        <div className="relative z-10 text-center w-full">
          <img src="/logo-bolt.png" alt="TOOLIO" className="h-20 mx-auto mb-10" style={{ transform: `scale(${1 + scrollY * 0.0002}) translateY(${scrollY * -0.08}px)` }} />

          <div className="space-y-1 mb-6">
            <h1 className="text-6xl font-black tracking-tight text-white leading-none">SCAN.</h1>
            <h1 className="text-6xl font-black tracking-tight text-yellow-500 leading-none">REGISTER.</h1>
            <h1 className="text-6xl font-black tracking-tight text-white leading-none">RECOVER.</h1>
          </div>

          <p className="text-gray-500 text-sm max-w-[260px] mx-auto mb-10 leading-relaxed">
            The AI-powered tool identification system. Protect what earns you a living.
          </p>

          <button className="bg-yellow-500 text-black font-bold px-10 py-4 rounded-full text-sm tracking-wider">START NOW</button>
        </div>

        <ChevronDown className="absolute bottom-8 left-1/2 -translate-x-1/2 w-6 h-6 text-white/20 animate-bounce" />
      </section>

      {/* Glass card preview */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1A] via-[#0d1525] to-[#0A0F1A]" />
        <div className="relative z-10">
          <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">Instant Registration</p>
          <h2 className="text-2xl font-bold text-center mb-8">One scan. Full protection.</h2>

          {/* Glass card */}
          <div className="max-w-sm mx-auto rounded-2xl bg-white/[0.04] border border-white/[0.08] backdrop-blur-xl p-6" style={{ transform: `translateY(${Math.max(0, (scrollY - 500) * -0.03)}px)` }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-green-400 font-medium">REGISTERED</span>
            </div>
            <div className="space-y-3 mb-5">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Manufacturer</span>
                <span className="text-sm font-bold text-yellow-500">MAKITA</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Model</span>
                <span className="text-sm font-medium">DTD152</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Serial</span>
                <span className="text-sm font-mono text-gray-400">MK-2024-78945</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Tool Type</span>
                <span className="text-sm font-medium">Impact Driver</span>
              </div>
              <div className="h-px bg-white/5" />
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">TOOLIO ID</span>
                <span className="text-xs font-mono text-yellow-500/70">Xf9jKya47aF799</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-yellow-500/10 rounded-lg p-3">
              <Shield className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-yellow-400 font-medium">Protected by TOOLIO</span>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard */}
      <section className="relative py-16 px-6">
        <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">Your Dashboard</p>
        <h2 className="text-2xl font-bold text-center mb-3">Total control</h2>
        <p className="text-gray-500 text-sm text-center mb-8">Track status, value, and history of every tool.</p>
        
        <div className="relative mx-auto max-w-sm rounded-xl overflow-hidden border border-white/10 shadow-2xl" style={{ transform: `perspective(800px) rotateY(${Math.min(3, Math.max(-3, (scrollY - 1000) * 0.01))}deg)` }}>
          <img src="/dashboard-preview.png" alt="Dashboard" className="w-full" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-transparent to-transparent" />
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 px-6">
        <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">Simple</p>
        <h2 className="text-2xl font-bold text-center mb-12">How it works</h2>

        {[
          { step: "01", icon: <Camera className="w-5 h-5" />, title: "Point & Scan", desc: "AI reads your tool label instantly" },
          { step: "02", icon: <Zap className="w-5 h-5" />, title: "Auto-Register", desc: "Manufacturer, model, serial — all captured" },
          { step: "03", icon: <Shield className="w-5 h-5" />, title: "Protected", desc: "Unique TOOLIO ID. Verified ownership." }
        ].map((item, i) => (
          <div key={i} className="flex gap-4 mb-8 last:mb-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-full border border-yellow-500/20 flex items-center justify-center">
              <span className="text-yellow-500 text-xs font-bold">{item.step}</span>
            </div>
            <div className="pt-1">
              <h3 className="font-bold text-sm mb-0.5">{item.title}</h3>
              <p className="text-gray-500 text-sm">{item.desc}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Stats bar */}
      <section className="py-12 px-6 bg-yellow-500">
        <div className="grid grid-cols-3 gap-4 text-center">
          {[
            { num: "10K+", label: "Tools" },
            { num: "50+", label: "Forces" },
            { num: "<60s", label: "To Scan" }
          ].map((s, i) => (
            <div key={i}>
              <div className="text-xl font-black text-black">{s.num}</div>
              <div className="text-xs text-black/60 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl font-black mb-3 leading-tight">Protect what<br/>earns you<br/>a living.</h2>
        <p className="text-gray-500 text-sm mb-8">Free. Fast. Trusted by the trade.</p>
        <button className="bg-yellow-500 text-black font-bold px-10 py-4 rounded-full text-sm tracking-wider">START NOW</button>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5 text-center">
        <img src="/logo-bolt.png" alt="TOOLIO" className="h-8 mx-auto mb-3" />
        <p className="text-xs text-gray-600">tool-io.uk</p>
      </footer>
    </div>
  );
}
